import { capitalizeFirstLetter, cloneNested } from '@/helpers/helpers'
import { computed, nextTick, ref } from '@vue/composition-api'
import dayjs from 'dayjs'
import useAppConfig from '@core/app-config/useAppConfig'
import store from '@/store/store'
import swal from 'sweetalert2'
import i18n from '@/libs/i18n'

export default () => {
  const { skin } = useAppConfig()
  const isDark = computed(() => skin.value === 'dark')
  const days = ref({})
  const optionsFetchPromises = {}
  const optimisticShiftId = ref(-1)

  const scheduleConfig = computed(() => store.getters['appConfig/config']?.schedule || {})
  const userRolesForScheduling = computed(() => scheduleConfig.value.userRolesForScheduling || [])
  const userRolesForTeam = computed(() => ['ROLE_DRIVER', 'ROLE_PARAMEDIC'])
  const shiftDefinitions = computed(() => scheduleConfig.value.shiftDefinitions || [])
  const showAllMatchingWorkersInPlanner = computed(() => Boolean(scheduleConfig.value.showAllMatchingWorkersInPlanner))
  const resolveShiftDefinitionId = shiftDefinition => shiftDefinition?.shiftDefinitionId ?? shiftDefinition?.id ?? shiftDefinition?.shiftDefinitionIdentifier
  const getScheduleForDay = (day, shiftDefinition) => {
    const schedules = day?.schedules || {}
    const resolvedId = resolveShiftDefinitionId(shiftDefinition)

    return schedules[resolvedId]
      || schedules[shiftDefinition?.id]
      || schedules[shiftDefinition?.shiftDefinitionId]
      || schedules[shiftDefinition?.shiftDefinitionIdentifier]
      || {
        isFulfilled: null,
        roles: {},
        teams: {},
      }
  }

  const currentMonth = ref(dayjs().startOf('month').add(1, 'month'))
  const periodStart = computed(() => currentMonth.value.startOf('month'))
  const periodEnd = computed(() => currentMonth.value.endOf('month'))

  const dayjsWeekdays = computed(() => {
    const weekdays = cloneNested(dayjs.localeData().weekdays())
    if (dayjs.localeData().firstDayOfWeek() === 1) {
      weekdays.push(weekdays.shift())
    }
    return weekdays
  })

  const toRoleSet = roles => new Set(Array.isArray(roles) ? roles : [])
  const normalizeParamedicRoles = roleSet => {
    if (roleSet.has('ROLE_FIRST_RESPONDER')) {
      roleSet.add('ROLE_PARAMEDIC')
    }
    return roleSet
  }
  const resolveDateFormatted = value => dayjs(value).format('YYYY-MM-DD')
  const getDayByDate = dateFormatted => days.value[dateFormatted]
  const findRoleMember = (teamShift, role) => teamShift?.members?.find(member => member?.role === role) || null
  const nextOptimisticShiftId = () => {
    optimisticShiftId.value -= 1
    return optimisticShiftId.value
  }

  const resetSchedules = dayFormatted => {
    const day = days.value[dayFormatted]
    if (!day) {
      return
    }
    Object.values(day.schedules).forEach(schedule => {
      const shiftDefinitionId = resolveShiftDefinitionId(schedule.shiftDefinition)
      if (!day.schedules[shiftDefinitionId]) {
        return
      }
      Object.values(schedule.roles).forEach(role => {
        day.schedules[shiftDefinitionId].roles[role.role].options = []
        day.schedules[shiftDefinitionId].roles[role.role].plannedShifts = []
        day.schedules[shiftDefinitionId].roles[role.role].isFallbackMode = false
        day.schedules[shiftDefinitionId].roles[role.role].pendingSelection = null
        day.schedules[shiftDefinitionId].roles[role.role].visibleOptions = []
      })
      Object.values(schedule.teams).forEach(team => {
        team.members.forEach((member, memberKey) => {
          day.schedules[shiftDefinitionId].teams[team.team.id].members[memberKey].selectedOption = null
          day.schedules[shiftDefinitionId].teams[team.team.id].members[memberKey].options = []
          day.schedules[shiftDefinitionId].teams[team.team.id].members[memberKey].isFallbackMode = false
          day.schedules[shiftDefinitionId].teams[team.team.id].members[memberKey].pendingSelection = null
          day.schedules[shiftDefinitionId].teams[team.team.id].members[memberKey].visibleOptions = []
        })
      })
    })
  }

  const fetchShiftsFulfillment = () => store.dispatch('schedules/fetchStaffingNeedsShiftsFulfillment', currentMonth.value)
    .then(response => {
      Object.keys(response.data).forEach(dateFormatted => {
        Object.keys(response.data[dateFormatted]).forEach(shiftDefinitionId => {
          const fulfillmentData = response.data[dateFormatted][shiftDefinitionId]
          if (!days.value[dateFormatted]) {
            return
          }
          if (!days.value[dateFormatted].schedules[shiftDefinitionId]) {
            return
          }

          const dayShiftsFulfillment = Object.values(response.data[dateFormatted])
            .reduce((totals, data) => ({
              required: totals.required + data.required,
              fulfilled: totals.fulfilled + data.fulfilled,
            }), { required: 0, fulfilled: 0 })

          if (dayShiftsFulfillment.required > 0) {
            days.value[dateFormatted].isFulfilled = dayShiftsFulfillment.required === dayShiftsFulfillment.fulfilled
          }
          if (fulfillmentData.required > 0) {
            days.value[dateFormatted].schedules[shiftDefinitionId].isFulfilled = fulfillmentData.required === fulfillmentData.fulfilled
          }
        })
      })
    })

  const isDriverOnlyTeam = teamShift => Boolean(teamShift?.team?.driverOnly)
  const canDriverActAsParamedic = teamShift => Boolean(teamShift?.team?.allowDriverAsParamedic)
  const isDriverActingAsParamedic = teamShift => Boolean(teamShift?.driverIsParamedic && canDriverActAsParamedic(teamShift) && !isDriverOnlyTeam(teamShift))
  const showDriverIsParamedicToggle = teamShift => Boolean(canDriverActAsParamedic(teamShift) && !isDriverOnlyTeam(teamShift))
  const shouldDisplayTeamMember = (teamShift, member) => {
    if (!teamShift || !member) {
      return false
    }
    if (member.role === 'ROLE_PARAMEDIC') {
      return !isDriverOnlyTeam(teamShift) && !isDriverActingAsParamedic(teamShift)
    }
    return true
  }
  const getTeamMemberLabel = (teamShift, member) => {
    if (!member) {
      return ''
    }
    if (member.role === 'ROLE_DRIVER' && isDriverActingAsParamedic(teamShift)) {
      return `${i18n.t('roles.ROLE_DRIVER')} / ${i18n.t('roles.ROLE_PARAMEDIC')}`
    }
    return i18n.t(`roles.${member.role}`)
  }
  const getTeamMemberByRole = (teamShift, role) => teamShift?.members?.find(member => member.role === role) || null
  const shouldRestoreDriverIsParamedic = teamShift => {
    if (!canDriverActAsParamedic(teamShift) || isDriverOnlyTeam(teamShift)) {
      return false
    }

    const driverMember = getTeamMemberByRole(teamShift, 'ROLE_DRIVER')
    const paramedicMember = getTeamMemberByRole(teamShift, 'ROLE_PARAMEDIC')
    const driverSelection = driverMember?.selectedOption
    if (!driverSelection) {
      return Boolean(teamShift?.driverIsParamedic)
    }
    if (driverSelection?.shift?.isDriverActingAsParamedic === true) {
      return true
    }
    if (paramedicMember?.selectedOption) {
      return false
    }

    return Boolean(teamShift?.driverIsParamedic)
  }
  const restoreDriverAsParamedicState = dayFormatted => {
    const day = days.value[dayFormatted]
    if (!day) {
      return
    }

    Object.keys(day.schedules).forEach(shiftDefinitionId => {
      const schedule = day.schedules[shiftDefinitionId]
      const updatedTeams = {}
      Object.keys(schedule.teams).forEach(teamDefinitionId => {
        const teamShift = schedule.teams[teamDefinitionId]
        updatedTeams[teamDefinitionId] = {
          ...teamShift,
          driverIsParamedic: shouldRestoreDriverIsParamedic(teamShift),
        }
      })
      day.schedules[shiftDefinitionId].teams = updatedTeams
    })
  }

  function isFallbackOption(option) {
    return Boolean(
      option?.isFallbackWithoutAvailability
      || option?.isPlannedWithoutAvailability
      || option?.shift?.isPlannedWithoutAvailability,
    )
  }
  function isSelectableOption(option) {
    return !option?.shift
  }
  function resolveOptionUserId(option) {
    return option?.user?.id ?? null
  }
  function getDeclaredOptions(options) {
    return options.filter(option => !isFallbackOption(option))
  }
  function getFallbackOptions(options) {
    return options.filter(option => isFallbackOption(option))
  }
  function hasSelectableDeclaredOptions(options) {
    return getDeclaredOptions(options).some(isSelectableOption)
  }
  function getAvailabilityOptionLabel(option) {
    return `${option?.user?.surname || ''} ${option?.user?.name || ''}`.trim()
  }
  function compareAvailabilityOptionLabels(left, right) {
    return getAvailabilityOptionLabel(left).localeCompare(getAvailabilityOptionLabel(right), 'pl', {
      sensitivity: 'base',
      ignorePunctuation: true,
    })
  }
  function sortAvailabilityOptions(options) {
    return [...options].sort((left, right) => {
      const selectableDiff = Number(isSelectableOption(right)) - Number(isSelectableOption(left))
      if (selectableDiff !== 0) {
        return selectableDiff
      }

      return compareAvailabilityOptionLabels(left, right)
    })
  }
  function filterFallbackOptionsWithoutDeclaredDuplicates(declaredOptions, fallbackOptions) {
    const declaredUserIds = new Set(
      declaredOptions
        .map(resolveOptionUserId)
        .filter(userId => userId !== null),
    )

    return fallbackOptions.filter(option => !declaredUserIds.has(resolveOptionUserId(option)))
  }
  function getVisibleOptions(options) {
    const declaredOptions = getDeclaredOptions(options)
    const fallbackOptions = getFallbackOptions(options)
    if (showAllMatchingWorkersInPlanner.value && fallbackOptions.length > 0) {
      return sortAvailabilityOptions([
        ...declaredOptions,
        ...filterFallbackOptionsWithoutDeclaredDuplicates(declaredOptions, fallbackOptions),
      ])
    }
    if (hasSelectableDeclaredOptions(options)) {
      return sortAvailabilityOptions(declaredOptions)
    }
    if (fallbackOptions.length > 0) {
      return sortAvailabilityOptions([
        ...declaredOptions,
        ...filterFallbackOptionsWithoutDeclaredDuplicates(declaredOptions, fallbackOptions),
      ])
    }
    return sortAvailabilityOptions(declaredOptions)
  }

  const rebuildVisibleOptionsForDay = dayFormatted => {
    const day = getDayByDate(dayFormatted)
    if (!day) {
      return
    }
    Object.keys(day.schedules).forEach(shiftDefinitionId => {
      const schedule = day.schedules[shiftDefinitionId]
      Object.keys(schedule.roles).forEach(role => {
        const roleShifts = schedule.roles[role]
        schedule.roles[role] = {
          ...roleShifts,
          visibleOptions: getVisibleOptions(roleShifts.options),
        }
      })
      Object.keys(schedule.teams).forEach(teamDefinitionId => {
        const teamShift = schedule.teams[teamDefinitionId]
        schedule.teams[teamDefinitionId] = {
          ...teamShift,
          members: teamShift.members.map(member => ({
            ...member,
            visibleOptions: getVisibleOptions(member.options),
          })),
        }
      })
    })
  }

  const fetchOptions = (dayFormatted, { force = false, showLoader = true } = {}) => {
    const day = getDayByDate(dayFormatted)
    if (!day) {
      return Promise.resolve()
    }
    if (!force && day.optionsLoaded) {
      return Promise.resolve()
    }
    if (optionsFetchPromises[dayFormatted]) {
      return optionsFetchPromises[dayFormatted]
    }

    day.loadingSchedules = showLoader
    const request = store.dispatch('schedules/fetchAvailabilitiesByQuery', {
      date: dayFormatted,
    })
      .then(response => {
        resetSchedules(dayFormatted)
        response.data.forEach(availabilityParam => {
          const availability = availabilityParam
          const availabilityRoleSet = normalizeParamedicRoles(toRoleSet(availability?.user?.roles))
          const userAvailabilityRolesForSchedule = userRolesForScheduling.value.filter(role => availabilityRoleSet.has(role))
          const userAvailabilityRolesForTeam = userRolesForTeam.value.filter(role => availabilityRoleSet.has(role))
          const dateFormatted = resolveDateFormatted(availability.date)
          const shiftDefinitionId = resolveShiftDefinitionId(availability)
          const dayData = days.value[dateFormatted]
          const schedule = dayData?.schedules?.[shiftDefinitionId]
          if (!schedule) {
            return
          }

          if (userAvailabilityRolesForSchedule.length) {
            userAvailabilityRolesForSchedule.forEach(role => {
              const roleShifts = schedule.roles[role]
              if (!roleShifts) {
                return
              }
              if (availability.shift?.asRole === role) {
                roleShifts.plannedShifts.push(availability)
              } else {
                if (availability.isFallbackWithoutAvailability) {
                  roleShifts.isFallbackMode = true
                }
                roleShifts.options.push(availability)
              }
            })
          }

          if (userAvailabilityRolesForTeam.length) {
            Object.values(schedule.teams).forEach(team => {
              team.members.forEach((member, memberKey) => {
                const isCombinedRole = member.role === 'ROLE_DRIVER' && isDriverActingAsParamedic(team)
                const hasRequiredRoles = userAvailabilityRolesForTeam.includes(member.role)
                  && availabilityRoleSet.has(member.role)
                  && (!isCombinedRole || availabilityRoleSet.has('ROLE_PARAMEDIC'))
                if (hasRequiredRoles) {
                  if (availability.shift?.asRole === member.role && availability.shift?.teamDefinition?.id === team.team.id) {
                    availability.isPlanned = true
                    schedule.teams[team.team.id].members[memberKey].selectedOption = availability
                  } else {
                    if (availability.isFallbackWithoutAvailability) {
                      schedule.teams[team.team.id].members[memberKey].isFallbackMode = true
                    }
                    schedule.teams[team.team.id].members[memberKey].options.push(availability)
                  }
                }
              })
            })
          }
        })
        restoreDriverAsParamedicState(dayFormatted)
        rebuildVisibleOptionsForDay(dayFormatted)
        day.optionsLoaded = true
      })
      .finally(() => {
        day.loadingSchedules = false
        delete optionsFetchPromises[dayFormatted]
      })
    optionsFetchPromises[dayFormatted] = request
    return request
  }

  const ensureDayOptionsLoaded = dayFormatted => fetchOptions(dayFormatted, { force: false, showLoader: true })

  const fetchStaffingNeeds = () => new Promise((resolve, reject) => {
    store.dispatch(
      'schedules/fetchStaffingNeeds',
      {
        dateFrom: currentMonth.value.startOf('month').format('YYYY-MM-DD'),
        dateTo: currentMonth.value.endOf('month').format('YYYY-MM-DD'),
      },
    )
      .then(response => {
        const staffingNeeds = response.data

        staffingNeeds.forEach(staffingNeed => {
          // Example staffingNeeds:     [{
          //         "id": 379,
          //         "type": "role",
          //         "date": "2025-03-01T00:00:00+01:00",
          //         "shiftDefinitionId": "7",
          //         "role": "ROLE_DISPATCHER",
          //         "quantity": 10,
          //         "teamDefinitions": []
          //     },
          //
          //     {
          //         "id": 381,
          //         "type": "team",
          //         "date": "2025-03-01T00:00:00+01:00",
          //         "shiftDefinitionId": "7",
          //         "teamDefinitions": [
          //             {
          //                 "id": 1,
          //                 "name": "T001"
          //             },
          //             {
          //                 "id": 3,
          //                 "name": "T003"
          //             }
          //         ]
          //     }]
          // const staffingNeed = _.cloneDeep(staffingNeedParam)
          // staffingNeed.shiftDefinition = shiftDefinitions.value.find(shiftDefinition => shiftDefinition.id === staffingNeed.shiftDefinitionId)
          const date = dayjs(staffingNeed.date)
          const dateFormatted = date.format('YYYY-MM-DD')
          const shiftDefinitionId = resolveShiftDefinitionId(staffingNeed)
          if (staffingNeed.type === 'role') {
            days.value[dateFormatted].schedules[shiftDefinitionId].roles[staffingNeed.role].headcounts = staffingNeed.quantity
          }
          if (staffingNeed.type === 'team') {
            staffingNeed.teamDefinitions.forEach(teamDefinition => {
              const members = [
                {
                  role: 'ROLE_DRIVER',
                  options: [],
                  selectedOption: null,
                  isFallbackMode: false,
                  pendingSelection: null,
                },
              ]
              if (!teamDefinition.driverOnly) {
                members.push({
                  role: 'ROLE_PARAMEDIC',
                  options: [],
                  selectedOption: null,
                  isFallbackMode: false,
                  pendingSelection: null,
                })
              }
              days.value[dateFormatted].schedules[shiftDefinitionId].teams[teamDefinition.id] = {
                team: teamDefinition,
                members,
                driverIsParamedic: false,
              }
            })
          }
        })
        resolve()
      })
      .catch(err => {
        window.toast.notify.danger(err.message)
        reject(err)
      })
  })

  const init = () => {
    // previouslyFetchedOptionParams = null
    const newDays = {}
    let date = periodStart.value.clone()
    const defaultSchedule = {}
    shiftDefinitions.value.forEach(shiftDefinition => {
      const shiftDefinitionId = resolveShiftDefinitionId(shiftDefinition)
      defaultSchedule[shiftDefinitionId] = {
        shiftDefinition,
        isFulfilled: null,
        roles: {},
        teams: {},
      }
      userRolesForScheduling.value.forEach(role => {
        defaultSchedule[shiftDefinitionId].roles[role] = {
          role,
          loading: true,
          headcounts: 0,
          options: [],
          plannedShifts: [],
          isFallbackMode: false,
          pendingSelection: null,
          visibleOptions: [],
        }
      })
    })

    while (date < periodEnd.value) {
      const dateString = date.format('YYYY-MM-DD')
      const isCurrentMonth = currentMonth.value.format('YYYYMM') === date.format('YYYYMM')

      newDays[dateString] = {
        date,
        isCurrentMonth,
        weekday: date.weekday(),
        weekNumber: date.week(),
        formatted: dateString,
        isToday: date.isToday(),
        isPast: date.isBefore(dayjs(), 'day'),
        schedules: cloneNested(defaultSchedule),
        loadingSchedules: true,
        optionsLoaded: false,
        isFulfilled: null,
      }

      date = date.add(1, 'day')
    }

    days.value = newDays
    fetchStaffingNeeds().then(() => {
      fetchOptions(periodStart.value.format('YYYY-MM-DD'))
      fetchShiftsFulfillment()
    })
  }
  init()

  const nextMonth = () => {
    currentMonth.value = currentMonth.value.add(1, 'month')
    init()
  }

  const prevMonth = () => {
    currentMonth.value = currentMonth.value.subtract(1, 'month')
    init()
  }

  const snapshotDay = dayFormatted => cloneNested(days.value[dayFormatted])
  const restoreDay = (dayFormatted, snapshot) => {
    if (snapshot) {
      days.value[dayFormatted] = snapshot
    }
  }
  const clearPendingSelectionAfterAddError = (dayFormatted, selectedOption, role, teamDefinitionId = null) => {
    const day = getDayByDate(dayFormatted)
    const shiftDefinitionId = resolveShiftDefinitionId(selectedOption)
    const schedule = day?.schedules?.[shiftDefinitionId]
    if (!schedule) {
      return
    }

    if (!teamDefinitionId) {
      if (schedule.roles[role]) {
        schedule.roles[role].pendingSelection = null
      }
      return
    }

    const teamShift = schedule.teams[teamDefinitionId]
    const member = findRoleMember(teamShift, role)
    if (member) {
      member.pendingSelection = null
    }
  }
  const removeOptionFromList = (options, optionToRemove) => {
    const optionId = optionToRemove?.id
    const optionUserId = optionToRemove?.user?.id
    return options.filter(option => {
      if (optionId && option.id) {
        return option.id !== optionId
      }
      return option?.user?.id !== optionUserId
    })
  }
  const createOptimisticShiftOption = (sourceOption, role, teamDefinitionId) => ({
    ...sourceOption,
    shift: {
      ...(sourceOption.shift || {}),
      id: sourceOption?.shift?.id || nextOptimisticShiftId(),
      asRole: role,
      teamDefinition: teamDefinitionId ? { id: Number(teamDefinitionId) } : sourceOption?.shift?.teamDefinition,
      isOptimistic: true,
    },
  })
  const applyOptimisticAddShift = ({
    selectedOption, role, teamDefinitionId = null, driverIsParamedic = false,
  }) => {
    const dayFormatted = resolveDateFormatted(selectedOption.date)
    const day = getDayByDate(dayFormatted)
    const shiftDefinitionId = resolveShiftDefinitionId(selectedOption)
    const schedule = day?.schedules?.[shiftDefinitionId]
    if (!schedule) {
      return null
    }
    const optimisticOption = createOptimisticShiftOption(selectedOption, role, teamDefinitionId)
    if (!teamDefinitionId) {
      const roleShifts = schedule.roles[role]
      if (!roleShifts) {
        return null
      }
      roleShifts.pendingSelection = null
      roleShifts.plannedShifts = [...roleShifts.plannedShifts, optimisticOption]
      roleShifts.options = removeOptionFromList(roleShifts.options, selectedOption)
      rebuildVisibleOptionsForDay(dayFormatted)
    } else {
      const teamShift = schedule.teams[teamDefinitionId]
      const member = findRoleMember(teamShift, role)
      if (!teamShift || !member) {
        return null
      }
      member.pendingSelection = null
      member.selectedOption = optimisticOption
      member.options = removeOptionFromList(member.options, selectedOption)
      if (role === 'ROLE_DRIVER') {
        teamShift.driverIsParamedic = Boolean(driverIsParamedic)
      }
      rebuildVisibleOptionsForDay(dayFormatted)
    }
    return optimisticOption
  }
  const patchOptimisticShiftWithServerData = (optimisticOption, serverResponse) => {
    const createdShift = Array.isArray(serverResponse?.data)
      ? serverResponse.data[0]
      : serverResponse?.data
    if (!createdShift || !optimisticOption?.shift) {
      return
    }
    const targetOption = optimisticOption
    targetOption.shift = {
      ...optimisticOption.shift,
      ...createdShift,
    }
    if (createdShift.availability) {
      targetOption.id = createdShift.availability.id
    }
  }

  const addShift = (selectedOption, role, teamDefinitionId = null, { driverIsParamedic = false } = {}) => {
    const dayFormatted = resolveDateFormatted(selectedOption.date)
    const daySnapshot = snapshotDay(dayFormatted)
    const optimisticOption = applyOptimisticAddShift({
      selectedOption, role, teamDefinitionId, driverIsParamedic,
    })
    const payload = selectedOption.isFallbackWithoutAvailability
      ? {
        userId: selectedOption.user.id,
        date: dayFormatted,
        shiftDefinitionId: resolveShiftDefinitionId(selectedOption),
        role,
      }
      : { availabilityIds: [selectedOption.id], role }
    if (teamDefinitionId) {
      payload.teamDefinitionId = teamDefinitionId
      if (role === 'ROLE_DRIVER') {
        payload.driverIsParamedic = Boolean(driverIsParamedic)
      }
    }
    return store.dispatch('schedules/addShifts', payload)
      .then(response => {
        patchOptimisticShiftWithServerData(optimisticOption, response)
        fetchShiftsFulfillment()
        fetchOptions(dayFormatted, { force: true, showLoader: false })
      })
      .catch(err => {
        restoreDay(dayFormatted, daySnapshot)
        clearPendingSelectionAfterAddError(dayFormatted, selectedOption, role, teamDefinitionId)
        window.toast.notify.danger(err.message)
        throw err
      })
  }

  const removeShift = (plannedShift, { shouldRefreshOptions = true } = {}) => {
    const dayFormatted = resolveDateFormatted(plannedShift.date)
    const daySnapshot = snapshotDay(dayFormatted)
    const shiftIdToRemove = plannedShift?.shift?.id
    const day = getDayByDate(dayFormatted)
    if (day) {
      Object.keys(day.schedules).forEach(shiftDefinitionId => {
        const schedule = day.schedules[shiftDefinitionId]
        Object.keys(schedule.roles).forEach(role => {
          const roleShifts = schedule.roles[role]
          schedule.roles[role] = {
            ...roleShifts,
            plannedShifts: roleShifts.plannedShifts.filter(item => item?.shift?.id !== shiftIdToRemove),
          }
        })
        Object.keys(schedule.teams).forEach(teamDefinitionId => {
          const team = schedule.teams[teamDefinitionId]
          schedule.teams[teamDefinitionId] = {
            ...team,
            members: team.members.map(member => {
              if (member?.selectedOption?.shift?.id === shiftIdToRemove) {
                return {
                  ...member,
                  selectedOption: null,
                }
              }
              return member
            }),
          }
        })
      })
      rebuildVisibleOptionsForDay(dayFormatted)
      if (shouldRefreshOptions) {
        day.loadingSchedules = true
      }
    }
    return store.dispatch('schedules/deleteShift', plannedShift.shift.id)
      .then(() => {
        if (shouldRefreshOptions) {
          fetchShiftsFulfillment()
          fetchOptions(dayFormatted, { force: true, showLoader: true })
        }
      })
      .catch(err => {
        restoreDay(dayFormatted, daySnapshot)
        window.toast.notify.danger(err.message)
        throw err
      })
  }

  const handleRoleOptionSelected = (option, role) => {
    if (!option) {
      return
    }
    addShift(option, role)
  }

  const handleTeamMemberOptionSelected = (option, member, teamDefinitionId, teamShift) => {
    if (!option) {
      return
    }
    addShift(option, member.role, teamDefinitionId, {
      driverIsParamedic: member.role === 'ROLE_DRIVER' && isDriverActingAsParamedic(teamShift),
    })
  }

  const handleSelectedTeamMemberInput = (value, selectedOption) => {
    if (value !== null || !selectedOption) {
      return
    }
    removeShift(selectedOption)
  }

  const handleSelectedRoleInput = (value, plannedShift) => {
    if (value !== null || !plannedShift) {
      return
    }
    removeShift(plannedShift)
  }

  const handleRoleOptionChanged = ({ from, to }, role) => {
    removeShift(from, { shouldRefreshOptions: false }).then(() => addShift(to, role))
  }

  const handleTeamMemberOptionChanged = ({ from, to }, member, teamDefinitionId, teamShift) => {
    removeShift(from, { shouldRefreshOptions: false }).then(() => addShift(
      to,
      member.role,
      teamDefinitionId,
      { driverIsParamedic: member.role === 'ROLE_DRIVER' && isDriverActingAsParamedic(teamShift) },
    ))
  }

  const clearTeamMembersSelection = async teamShift => {
    if (!teamShift?.members?.length) {
      return false
    }
    let removedAny = false
    const pendingRemovals = []
    const nextMembers = teamShift.members.map(member => {
      if (!member) {
        return member
      }
      const plannedShift = member.selectedOption
      if (plannedShift) {
        removedAny = true
        pendingRemovals.push(removeShift(plannedShift, { shouldRefreshOptions: false }))
      }
      return {
        ...member,
        selectedOption: null,
        options: [],
      }
    })
    teamShift.members.splice(0, teamShift.members.length, ...nextMembers)
    if (pendingRemovals.length) {
      await Promise.all(pendingRemovals)
    }
    return removedAny
  }

  const handleDriverIsParamedicToggle = async ({
    dayFormatted,
    shiftDefinitionId,
    teamDefinitionId,
    value,
  }) => {
    const daySchedules = days.value[dayFormatted]
    if (!daySchedules) {
      return
    }
    const shiftDefinitionSchedules = daySchedules.schedules[shiftDefinitionId]
    if (!shiftDefinitionSchedules) {
      return
    }
    const teamShift = shiftDefinitionSchedules.teams[teamDefinitionId]
    if (!teamShift) {
      return
    }
    const day = getDayByDate(dayFormatted)
    const daySnapshot = snapshotDay(dayFormatted)
    if (day) {
      day.loadingSchedules = true
    }
    teamShift.driverIsParamedic = Boolean(value)
    try {
      await clearTeamMembersSelection(teamShift)
      shiftDefinitionSchedules.teams = {
        ...shiftDefinitionSchedules.teams,
      }
      fetchShiftsFulfillment()
      fetchOptions(dayFormatted, { force: true, showLoader: true })
    } catch (err) {
      restoreDay(dayFormatted, daySnapshot)
      throw err
    }
  }
  const allMembersSelected = teamShift => {
    if (!teamShift) {
      return false
    }
    const relevantMembers = teamShift.members.filter(member => shouldDisplayTeamMember(teamShift, member))
    if (!relevantMembers.length) {
      return false
    }
    return relevantMembers.every(member => member.selectedOption)
  }

  const getVisibleRoleOptions = roleShifts => roleShifts?.visibleOptions || getVisibleOptions(roleShifts?.options || [])
  const getVisibleMemberOptions = member => member?.visibleOptions || getVisibleOptions(member?.options || [])
  const hasSelectableVisibleOptions = options => options.some(isSelectableOption)
  const hasSelectableRoleOptions = roleShifts => hasSelectableVisibleOptions(getVisibleRoleOptions(roleShifts))
  const hasSelectableMemberOptions = member => hasSelectableVisibleOptions(getVisibleMemberOptions(member))

  const scrollToCollapse = elementId => {
    nextTick(() => {
      const collapseElement = document.getElementById(elementId)
      if (collapseElement) {
        const offset = 180 // Set scroll above
        const elementPosition = collapseElement.getBoundingClientRect().top + window.scrollY
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' })
      }
    })
  }

  const generateSchedule = (day = null) => {
    let period = currentMonth.value.format('MMMM YYYY')
    if (day) {
      period = day.format('D MMMM YYYY')
    }
    swal.fire({
      title: `${i18n.t('schedule.generate.alert.title')}<br /><span class="text-muted font-medium-3">${period}</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: i18n.t('schedule.generate.alert.confirm'),
      cancelButtonText: i18n.t('shared.button.cancel'),
      reverseButtons: true,
      input: 'checkbox',
      inputValue: 0,
      inputPlaceholder: i18n.t('schedule.generate.alert.reset-checkbox'),
      customClass: {
        confirmButton: 'btn btn-success mx-1',
        cancelButton: 'btn btn-danger mx-1',
      },
    }).then(result => {
      if (result.isConfirmed) {
        let dateFrom = currentMonth.value.startOf('month').format('YYYY-MM-DD')
        let dateTo = currentMonth.value.endOf('month').format('YYYY-MM-DD')
        if (day) {
          dateFrom = day.startOf('day').format('YYYY-MM-DD')
          dateTo = day.endOf('day').format('YYYY-MM-DD')
        }

        store.dispatch('schedules/generateSchedule', { dateFrom, dateTo, override: result.value })
          .then(() => {
            window.toast.notify.success(i18n.t('schedule.generate.alert.success'))
          })
          .catch(err => {
            window.toast.notify.danger(err.message)
            throw err
          })
          .finally(() => {
            if (day) {
              fetchOptions(day.format('YYYY-MM-DD'), { force: true })
            } else {
              fetchOptions(dateFrom, { force: true })
            }
          })
      }
    })
  }

  return {
    isDark,
    shiftDefinitions,
    resolveShiftDefinitionId,
    getScheduleForDay,

    currentMonth,
    dayjsWeekdays,
    days,

    nextMonth,
    prevMonth,

    fetchOptions,
    ensureDayOptionsLoaded,
    addShift,
    removeShift,
    generateSchedule,
    allMembersSelected,
    shouldDisplayTeamMember,
    showDriverIsParamedicToggle,
    getTeamMemberLabel,
    isFallbackOption,
    isSelectableOption,
    getAvailabilityOptionLabel,
    getVisibleRoleOptions,
    getVisibleMemberOptions,
    hasSelectableRoleOptions,
    hasSelectableMemberOptions,
    handleRoleOptionSelected,
    handleTeamMemberOptionSelected,
    handleSelectedTeamMemberInput,
    handleSelectedRoleInput,
    handleRoleOptionChanged,
    handleTeamMemberOptionChanged,
    handleDriverIsParamedicToggle,
    scrollToCollapse,

    dayjs,
    capitalizeFirstLetter,
  }
}
